import {
  Alert, Button, Checkbox, DatePicker, Grid, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, TimePicker, Tooltip, Typography,
} from 'antd';
import dayjs from 'dayjs';
import notificationCampaignApi from 'api/notificationCampaignApi';
import ToastHelper from 'general/helpers/ToastHelper';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

const { Text } = Typography;

// The CMS sidebar/header sit above antd's default modal layer (1000) and covered the left edge of the form.
const MODAL_Z_INDEX = 2100;

const STATUS_META = {
  DRAFT: { color: 'default', label: 'Nháp' },
  ACTIVE: { color: 'green', label: 'Đang chạy' },
  PAUSED: { color: 'orange', label: 'Tạm dừng' },
  ENDED: { color: 'default', label: 'Đã kết thúc' },
};
const RUN_META = {
  DONE: { color: 'green', label: 'Hoàn tất' },
  RUNNING: { color: 'blue', label: 'Đang gửi' },
  FAILED: { color: 'red', label: 'Lỗi' },
  SKIPPED: { color: 'default', label: 'Bỏ qua' },
};
const ROLE_OPTIONS = [
  { value: 'CUSTOMER', label: 'Khách hàng (mọi người dùng app)' },
  { value: 'RIDE_DRIVER', label: 'Tài xế (đã được duyệt)' },
  { value: 'ALL', label: 'Tất cả' },
];
const FILTER_OPTIONS = [
  { value: '', label: 'Không lọc (cả nhóm)' },
  { value: 'WALLET_LOW', label: 'Ví còn ít tiền (và vừa sạc gần đây)' },
  { value: 'INACTIVE_CHARGE', label: 'Lâu chưa sạc xe' },
  { value: 'NEW_USER', label: 'Người dùng mới đăng ký' },
];
const FILTER_VALUE_LABEL = {
  WALLET_LOW: 'Số dư dưới (đồng)',
  INACTIVE_CHARGE: 'Số ngày chưa sạc từ',
  NEW_USER: 'Đăng ký trong (ngày)',
};
const FILTER_VALUE_DEFAULT = { WALLET_LOW: 50000, INACTIVE_CHARGE: 7, NEW_USER: 1 };
// Ready-made behaviour campaigns: pre-fill the form, nothing is saved until the admin confirms.
const TEMPLATES = [
  {
    label: 'Nhắc nạp ví',
    values: {
      name: 'Nhắc nạp ví', title: 'Số dư ví EVG sắp hết', message: 'Ví của bạn còn ít tiền. Nạp thêm để sạc xe thuận tiện hơn.',
      category: 'CHARGE', isMarketing: true, role: 'CUSTOMER', scheduleType: 'DAILY', sendTime: '09:00', filterType: 'WALLET_LOW', filterValue: 50000, cooldownDays: 7,
    },
  },
  {
    label: 'Lâu chưa sạc',
    values: {
      name: 'Nhắc lâu chưa sạc', title: 'Lâu rồi chưa thấy bạn ghé trạm sạc', message: 'Mở bản đồ EVG để tìm trạm sạc gần bạn nhất.',
      category: 'PROMOTION', isMarketing: true, role: 'CUSTOMER', scheduleType: 'DAILY', sendTime: '10:00', filterType: 'INACTIVE_CHARGE', filterValue: 14, cooldownDays: 14,
    },
  },
  {
    label: 'Giới thiệu xe ghép',
    values: {
      name: 'Giới thiệu xe ghép', title: 'Đi xe ghép tiết kiệm hơn', message: 'Đặt chuyến xe ghép hẹn giờ ngay trong ứng dụng EVG: chọn điểm đón, điểm đến và giờ đi, tài xế sẽ nhận chuyến cho bạn.',
      category: 'PROMOTION', isMarketing: true, role: 'CUSTOMER', scheduleType: 'WEEKLY', daysOfWeek: [5], sendTime: '09:00', filterType: '', cooldownDays: 0,
    },
  },
  {
    label: 'Xe ghép: tài xế đăng chuyến',
    values: {
      name: 'Nhắc tài xế đăng chuyến xe ghép', title: 'Có khách đang tìm chuyến xe ghép', message: 'Mở mục Xe ghép trong ứng dụng để xem các yêu cầu hẹn giờ đang chờ và đăng chuyến của bạn để nhận khách.',
      category: 'OPERATION', isMarketing: false, role: 'RIDE_DRIVER', scheduleType: 'DAILY', sendTime: '08:00', filterType: '', cooldownDays: 0,
    },
  },
  {
    label: 'Xe ghép: nhắc bắt đầu chuyến',
    values: {
      name: 'Nhắc tài xế bắt đầu chuyến đúng giờ', title: 'Đừng quên bắt đầu chuyến đúng giờ', message: 'Tới giờ khởi hành, hãy mở chuyến của bạn và bấm "Bắt đầu chuyến". Chuyến không được bắt đầu sau 12 giờ sẽ bị hủy và hoàn tiền cho khách.',
      category: 'OPERATION', isMarketing: false, role: 'RIDE_DRIVER', scheduleType: 'DAILY', sendTime: '07:30', filterType: '', cooldownDays: 0,
    },
  },
  {
    label: 'Chào mừng người mới',
    values: {
      name: 'Chào mừng người mới', title: 'Chào mừng bạn đến với EVG', message: 'Thêm xe của bạn và bắt đầu sạc tại các trạm EVG.',
      category: 'OPERATION', isMarketing: false, role: 'CUSTOMER', scheduleType: 'DAILY', sendTime: '09:30', filterType: 'NEW_USER', filterValue: 1, cooldownDays: 30,
    },
  },
];
const SCHEDULE_OPTIONS = [
  { value: 'ONCE', label: 'Một lần' },
  { value: 'DAILY', label: 'Hằng ngày' },
  { value: 'WEEKLY', label: 'Hằng tuần' },
  { value: 'MONTHLY', label: 'Hằng tháng' },
];
const CATEGORY_OPTIONS = [
  { value: 'PROMOTION', label: 'Khuyến mãi' },
  { value: 'OPERATION', label: 'Vận hành' },
  { value: 'CHARGE', label: 'Sạc xe' },
  { value: 'BUSINESS', label: 'Kinh doanh' },
  { value: 'OTHER', label: 'Khác' },
];
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'T2' }, { value: 2, label: 'T3' }, { value: 3, label: 'T4' }, { value: 4, label: 'T5' },
  { value: 5, label: 'T6' }, { value: 6, label: 'T7' }, { value: 7, label: 'CN' },
];
const WEEKDAY_LABEL = Object.fromEntries(WEEKDAY_OPTIONS.map((o) => [o.value, o.label]));

const errorMessage = (error) => error?.response?.data?.reason || error?.reason || error?.message || 'Có lỗi xảy ra';

const roleLabel = (row) => {
  if (row.role === 'ALL') return 'Tất cả';
  if (row.role === 'RIDE_DRIVER') return 'Tài xế';
  if (row.role === 'CUSTOMER') return 'Khách hàng';
  if (row.role === 'DRIVER') return 'Khách hàng & tài xế (cũ)';
  return 'Đối tác (cũ)';
};

// "Số dư dưới 50.000đ, vừa sạc gần đây" — shared by the summary panel and the list.
const describeFilter = (row) => {
  const n = Number(row.filterValue || 0).toLocaleString('vi-VN');
  if (row.filterType === 'WALLET_LOW') return `Số dư dưới ${n}đ và vừa sạc gần đây`;
  if (row.filterType === 'INACTIVE_CHARGE') return `Chưa sạc xe từ ${n} ngày`;
  if (row.filterType === 'NEW_USER') return `Đăng ký trong ${n} ngày qua`;
  return '';
};
const filterLabel = (row) => (row.filterType ? ` · ${describeFilter(row)}` : '');

const scheduleLabel = (row) => {
  const time = row.sendTime;
  if (row.scheduleType === 'ONCE') return `Một lần · ${moment(row.startsAt).format('HH:mm DD/MM/YYYY')}`;
  if (row.scheduleType === 'DAILY') return `Hằng ngày · ${time}`;
  if (row.scheduleType === 'WEEKLY') return `Hằng tuần (${(row.daysOfWeek || []).map((d) => WEEKDAY_LABEL[d]).join(', ')}) · ${time}`;
  return `Hằng tháng · ngày ${row.dayOfMonth} · ${time}`;
};

const EMPTY_FORM = {
  name: '', title: '', message: '', category: 'PROMOTION', isMarketing: true, link: '',
  role: 'CUSTOMER', subRole: '', scheduleType: 'DAILY', sendTime: '09:00', daysOfWeek: [1], dayOfMonth: 1,
  startsAt: null, endsAt: null, filterType: '', filterValue: 50000, cooldownDays: 0,
};

const fmtNumber = (n) => Number(n || 0).toLocaleString('vi-VN');
const FILTER_UNIT = { WALLET_LOW: 'đ', INACTIVE_CHARGE: 'ngày', NEW_USER: 'ngày' };
const QUIET_FROM = 21;
const QUIET_TO = 7;
const isQuietTime = (hhmm) => { const h = Number(String(hhmm || '').slice(0, 2)); return h >= QUIET_FROM || h < QUIET_TO; };

// ---- small presentational pieces -------------------------------------------------
const C = { border: '#e4e7ec', muted: '#667085', text: '#101828', soft: '#f9fafb', primary: '#1d4ed8', danger: '#d92d20' };

function Section({ index, title, subtitle, children, first }) {
  return (
    <section style={{ paddingTop: first ? 0 : 28, marginTop: first ? 0 : 28, borderTop: first ? 'none' : `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{index}</span>
        <span style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{title}</span>
        {subtitle ? <span style={{ fontSize: 12, color: C.muted }}>{subtitle}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, helper, error, children, style }) {
  return (
    <div style={{ marginBottom: 20, ...style }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: C.text, marginBottom: 8 }}>
        {label}
        {required ? <span style={{ color: C.danger, marginLeft: 3 }}>*</span> : null}
      </div>
      {children}
      {error ? <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>{error}</div>
        : helper ? <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{helper}</div> : null}
    </div>
  );
}

function Grid2({ columns = '1fr 1fr', children, stacked }) {
  return <div style={{ display: 'grid', gridTemplateColumns: stacked ? '1fr' : columns, columnGap: 20 }}>{children}</div>;
}

function CampaignFormModal({ open, campaign, onClose, onSaved }) {
  const screens = Grid.useBreakpoint();
  const wide = screens.lg !== false; // single column below ~992px
  const [values, setValues] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [audience, setAudience] = useState(null);
  const [audienceState, setAudienceState] = useState('idle'); // idle | loading | error
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAudience(null);
    setAudienceState('idle');
    setSelectedTemplate('');
    setShowErrors(false);
    setServerError('');
    setValues(campaign ? {
      ...EMPTY_FORM, ...campaign, subRole: '', link: campaign.link || '',
      daysOfWeek: campaign.daysOfWeek || [1], dayOfMonth: campaign.dayOfMonth || 1,
      startsAt: campaign.startsAt ? dayjs(campaign.startsAt) : null, endsAt: campaign.endsAt ? dayjs(campaign.endsAt) : null,
      filterType: campaign.filterType || '', filterValue: campaign.filterValue ?? 50000,
      cooldownDays: Math.round((campaign.cooldownHours || 0) / 24),
    } : EMPTY_FORM);
  }, [open, campaign]);

  const set = (patch) => setValues((value) => ({ ...value, ...patch }));
  const isOnce = values.scheduleType === 'ONCE';
  const status = campaign?.status || 'DRAFT';

  // ---- validation (mirrors the server rules; the server stays the authority) ----
  const errors = useMemo(() => {
    const e = {};
    if (!values.name.trim()) e.name = 'Vui lòng nhập tên chiến dịch';
    if (!values.title.trim()) e.title = 'Vui lòng nhập tiêu đề thông báo';
    if (!values.message.trim()) e.message = 'Vui lòng nhập nội dung thông báo';
    if (isOnce && !values.startsAt) e.startsAt = 'Vui lòng chọn ngày và giờ gửi';
    if (!isOnce && !values.sendTime) e.sendTime = 'Vui lòng chọn giờ gửi';
    const timeToCheck = isOnce ? (values.startsAt ? values.startsAt.format('HH:mm') : '') : values.sendTime;
    if (values.isMarketing && timeToCheck && isQuietTime(timeToCheck)) e.sendTime = `Thông báo quảng bá không được gửi từ ${QUIET_FROM}:00 đến 0${QUIET_TO}:00`;
    if (values.scheduleType === 'WEEKLY' && !values.daysOfWeek.length) e.daysOfWeek = 'Chọn ít nhất một ngày trong tuần';
    if (values.scheduleType === 'MONTHLY' && !(Number(values.dayOfMonth) >= 1 && Number(values.dayOfMonth) <= 31)) e.dayOfMonth = 'Ngày trong tháng từ 1 đến 31';
    if (values.filterType && !(Number(values.filterValue) >= 0)) e.filterValue = 'Nhập một số không âm';
    if (values.filterType && Number(values.cooldownDays) < 1) e.cooldownDays = 'Cần giãn cách ít nhất 1 ngày khi có điều kiện lọc';
    if (!isOnce && values.startsAt && values.endsAt && values.endsAt.isBefore(values.startsAt)) e.endsAt = 'Thời điểm kết thúc phải sau thời điểm bắt đầu';
    return e;
  }, [values, isOnce]);
  const valid = Object.keys(errors).length === 0;
  const missing = Object.values(errors);
  const shown = (key) => ((showErrors || key === 'sendTime') ? errors[key] : undefined);

  // ---- recipients estimate: refreshes itself whenever the audience settings change ----
  const audienceKey = [values.role, values.filterType, values.filterType ? values.filterValue : '', values.isMarketing, values.cooldownDays].join('|');
  const refreshAudience = useCallback(async () => {
    setAudienceState('loading');
    try {
      const response = await notificationCampaignApi.previewAudience({
        role: values.role, subRole: null,
        filterType: values.filterType || null, filterValue: values.filterType ? Number(values.filterValue) : null,
        isMarketing: values.isMarketing, cooldownHours: Number(values.cooldownDays || 0) * 24,
        campaignId: campaign?.campaignId,
      });
      setAudience(response?.audience || null);
      setAudienceState('idle');
    } catch (e) { setAudienceState('error'); }
  }, [audienceKey, campaign?.campaignId]);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(refreshAudience, 400);
    return () => clearTimeout(timer);
  }, [open, refreshAudience]);

  const payload = () => ({
    name: values.name.trim(), title: values.title.trim(), message: values.message.trim(), category: values.category,
    isMarketing: values.isMarketing, link: values.link || null, role: values.role, subRole: null,
    scheduleType: values.scheduleType,
    sendTime: isOnce && values.startsAt ? values.startsAt.format('HH:mm') : values.sendTime,
    daysOfWeek: values.scheduleType === 'WEEKLY' ? values.daysOfWeek : null,
    dayOfMonth: values.scheduleType === 'MONTHLY' ? Number(values.dayOfMonth) : null,
    startsAt: values.startsAt ? values.startsAt.toISOString() : null, endsAt: !isOnce && values.endsAt ? values.endsAt.toISOString() : null,
    filterType: values.filterType || null,
    filterValue: values.filterType ? Number(values.filterValue) : null,
    cooldownHours: Number(values.cooldownDays || 0) * 24,
  });

  const sendTest = async () => {
    try {
      const response = await notificationCampaignApi.sendTest({ title: values.title, message: values.message, link: values.link });
      ToastHelper.showSuccess(`Đã gửi thử tới ${response?.delivered || 0}/${response?.devices || 0} thiết bị của bạn`);
    } catch (e) { ToastHelper.showError(errorMessage(e)); }
  };

  // mode: 'draft' (save only) | 'activate' (save, then activate / resume)
  const submit = async (mode) => {
    setShowErrors(true);
    setServerError('');
    if (!valid) return;
    setSaving(true);
    try {
      let id = campaign?.campaignId;
      if (campaign) await notificationCampaignApi.updateCampaign(id, payload());
      else id = (await notificationCampaignApi.createCampaign(payload()))?.campaign?.campaignId;
      if (mode === 'activate') {
        if (status === 'PAUSED') await notificationCampaignApi.resume(id);
        else if (status === 'DRAFT') await notificationCampaignApi.activate(id);
      }
      ToastHelper.showSuccess(mode === 'activate' ? 'Đã lưu và kích hoạt chiến dịch' : 'Đã lưu chiến dịch');
      onSaved();
    } catch (e) { setServerError(errorMessage(e)); } finally { setSaving(false); }
  };

  const confirmActivate = () => {
    setShowErrors(true);
    if (!valid) return;
    Modal.confirm({
      title: status === 'PAUSED' ? 'Tiếp tục chạy chiến dịch?' : 'Kích hoạt chiến dịch?',
      content: `Chiến dịch sẽ tự gửi ${scheduleLabel({ ...values, startsAt: values.startsAt ? values.startsAt.toISOString() : null })}${audience ? ` cho khoảng ${fmtNumber(audience.total)} người` : ''}.`,
      okText: 'Kích hoạt', cancelText: 'Xem lại',
      onOk: () => submit('activate'),
      zIndex: MODAL_Z_INDEX + 10,
    });
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template.label);
    setValues({ ...EMPTY_FORM, ...template.values });
    setShowErrors(false);
  };

  // ---- derived text for the side panel ----
  const filterText = describeFilter(values) || 'Không lọc, cả nhóm';
  const timeText = isOnce ? (values.startsAt ? values.startsAt.format('HH:mm DD/MM/YYYY') : '—') : values.sendTime || '—';
  const frequencyText = { ONCE: 'Một lần', DAILY: 'Hằng ngày', WEEKLY: `Hằng tuần (${values.daysOfWeek.map((d) => WEEKDAY_LABEL[d]).join(', ') || '—'})`, MONTHLY: `Hằng tháng, ngày ${values.dayOfMonth}` }[values.scheduleType];
  const previewTime = isOnce ? (values.startsAt ? values.startsAt.format('HH:mm') : '') : values.sendTime;

  const left = (
    <div style={{ minWidth: 0 }}>
      {serverError && <Alert type="error" showIcon message={serverError} style={{ marginBottom: 20 }} />}

      <Section first index="01" title="Nội dung thông báo">
        {!campaign && (
          <Field label="Bắt đầu nhanh từ mẫu">
            <Space wrap size={[8, 8]}>
              {TEMPLATES.map((template) => (
                <Tag.CheckableTag
                  key={template.label} checked={selectedTemplate === template.label}
                  onChange={() => applyTemplate(template)}
                  style={{ padding: '5px 14px', fontSize: 13, border: `1px solid ${selectedTemplate === template.label ? C.primary : C.border}`, borderRadius: 16 }}
                >
                  {template.label}
                </Tag.CheckableTag>
              ))}
            </Space>
          </Field>
        )}
        <Field label="Tên chiến dịch" required helper="Chỉ dùng để quản lý nội bộ, người nhận sẽ không nhìn thấy tên này." error={shown('name')}>
          <Input size="large" status={shown('name') ? 'error' : ''} value={values.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Tiêu đề thông báo" required helper="Dòng tiêu đề người dùng nhìn thấy trên điện thoại." error={shown('title')}>
          <Input size="large" status={shown('title') ? 'error' : ''} value={values.title} maxLength={256} onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <Field label="Nội dung" required error={shown('message')}>
          <Input.TextArea status={shown('message') ? 'error' : ''} value={values.message} rows={4} maxLength={2048} showCount onChange={(e) => set({ message: e.target.value })} />
        </Field>
        <Grid2 stacked={!wide}>
          <Field label="Danh mục thông báo" helper="Phân loại thông báo trong hộp thư của người dùng.">
            <Select
              size="large" style={{ width: '100%' }} value={values.category} options={CATEGORY_OPTIONS}
              onChange={(category) => set({ category, isMarketing: category === 'PROMOTION' ? true : values.isMarketing })}
            />
          </Field>
          <Field label="Liên kết khi bấm" helper="Tuỳ chọn.">
            <Input size="large" value={values.link} maxLength={512} placeholder="https://... hoặc deeplink" onChange={(e) => set({ link: e.target.value })} />
          </Field>
        </Grid2>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Switch checked={values.isMarketing} onChange={(isMarketing) => set({ isMarketing })} style={{ marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>Thông báo quảng bá</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              Bật nếu nội dung mang tính quảng bá. Hệ thống sẽ không gửi từ {QUIET_FROM}:00 đến 0{QUIET_TO}:00, bỏ qua người đã tắt khuyến mãi và giới hạn một thông báo quảng bá mỗi 20 giờ cho mỗi người.
            </div>
          </div>
        </div>
      </Section>

      <Section index="02" title="Đối tượng nhận">
        <Field label="Gửi cho">
          <Select size="large" style={{ width: '100%' }} value={values.role} options={ROLE_OPTIONS} onChange={(role) => set({ role })} />
        </Field>
        <Field label="Điều kiện lọc theo hành vi" helper="Chỉ gửi cho những người thoả điều kiện bên dưới.">
          <Select
            size="large" style={{ width: '100%' }} value={values.filterType} options={FILTER_OPTIONS}
            onChange={(filterType) => set({
              filterType,
              filterValue: filterType ? FILTER_VALUE_DEFAULT[filterType] : values.filterValue,
              cooldownDays: filterType && !values.cooldownDays ? 7 : values.cooldownDays,
            })}
          />
        </Field>
        <Grid2 stacked={!wide}>
          {values.filterType ? (
            <Field label={FILTER_VALUE_LABEL[values.filterType].replace(' (đồng)', '')} error={shown('filterValue')}>
              <InputNumber
                size="large" style={{ width: '100%' }} min={0} value={values.filterValue}
                addonAfter={FILTER_UNIT[values.filterType]} status={shown('filterValue') ? 'error' : ''}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={(v) => `${v}`.replace(/\./g, '')}
                onChange={(filterValue) => set({ filterValue })}
              />
            </Field>
          ) : null}
          <Field
            label="Khoảng nghỉ giữa hai lần gửi" error={shown('cooldownDays')}
            helper="Một người sẽ không nhận lại chiến dịch này trong khoảng thời gian trên."
          >
            <InputNumber
              size="large" style={{ width: '100%' }} min={0} value={values.cooldownDays} addonAfter="ngày"
              status={shown('cooldownDays') ? 'error' : ''} onChange={(cooldownDays) => set({ cooldownDays })}
            />
          </Field>
        </Grid2>
        <div style={{ border: `1px solid ${C.border}`, background: C.soft, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: C.muted }}>Người nhận dự kiến</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: audience?.overLimit ? C.danger : C.text, lineHeight: '32px' }}>
              {audienceState === 'loading' && !audience ? '…' : audienceState === 'error' ? '—' : `${fmtNumber(audience?.total)} người`}
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>
              {audience?.overLimit
                ? `Vượt giới hạn ${fmtNumber(audience.limit)} người mỗi lần gửi — lần gửi sẽ bị chặn.`
                : audienceState === 'error' ? 'Không tính được, bấm Làm mới để thử lại.'
                  : `Chỉ tính người đang bật nhận thông báo.${audience?.skippedOptOut ? ` Bỏ ${fmtNumber(audience.skippedOptOut)} người đã tắt khuyến mãi.` : ''}${audience?.skippedCapped ? ` Bỏ ${fmtNumber(audience.skippedCapped)} người vừa nhận gần đây.` : ''}`}
            </div>
          </div>
          <Button type="link" loading={audienceState === 'loading'} onClick={refreshAudience}>Làm mới</Button>
        </div>
      </Section>

      <Section index="03" title="Lịch gửi" subtitle="Múi giờ: GMT+7 · Việt Nam">
        <Grid2 stacked={!wide}>
          <Field label="Tần suất">
            <Select size="large" style={{ width: '100%' }} value={values.scheduleType} options={SCHEDULE_OPTIONS} onChange={(scheduleType) => set({ scheduleType })} />
          </Field>
          {isOnce ? (
            <Field label="Ngày và giờ gửi" required error={shown('startsAt') || shown('sendTime')}>
              <DatePicker
                size="large" showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" placeholder="Chọn ngày và giờ" style={{ width: '100%' }}
                status={shown('startsAt') || shown('sendTime') ? 'error' : ''} value={values.startsAt} onChange={(v) => set({ startsAt: v })}
              />
            </Field>
          ) : (
            <Field label="Giờ gửi" required error={shown('sendTime')}>
              <TimePicker
                size="large" format="HH:mm" placeholder="Chọn giờ" style={{ width: '100%' }} allowClear={false}
                status={shown('sendTime') ? 'error' : ''}
                value={values.sendTime ? dayjs(values.sendTime, 'HH:mm') : null} onChange={(v) => set({ sendTime: v ? v.format('HH:mm') : '' })}
              />
            </Field>
          )}
        </Grid2>
        {values.scheduleType === 'WEEKLY' && (
          <Field label="Các ngày trong tuần" error={shown('daysOfWeek')}>
            <Checkbox.Group options={WEEKDAY_OPTIONS} value={values.daysOfWeek} onChange={(daysOfWeek) => set({ daysOfWeek })} />
          </Field>
        )}
        {values.scheduleType === 'MONTHLY' && (
          <Field label="Ngày trong tháng" helper="Tháng không có ngày này (ví dụ 31) sẽ gửi vào ngày cuối tháng." error={shown('dayOfMonth')} style={{ maxWidth: 240 }}>
            <InputNumber size="large" style={{ width: '100%' }} min={1} max={31} value={values.dayOfMonth} onChange={(dayOfMonth) => set({ dayOfMonth })} />
          </Field>
        )}
        {!isOnce && (
          <Grid2 stacked={!wide}>
            <Field label="Bắt đầu từ" helper="Tuỳ chọn.">
              <DatePicker size="large" showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" placeholder="Chọn ngày và giờ" style={{ width: '100%' }} value={values.startsAt} onChange={(v) => set({ startsAt: v })} />
            </Field>
            <Field label="Kết thúc lúc" helper="Tuỳ chọn." error={shown('endsAt')}>
              <DatePicker size="large" showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" placeholder="Chọn ngày và giờ" style={{ width: '100%' }} status={shown('endsAt') ? 'error' : ''} value={values.endsAt} onChange={(v) => set({ endsAt: v })} />
            </Field>
          </Grid2>
        )}
      </Section>

    </div>
  );

  const statusMeta = STATUS_META[status] || STATUS_META.DRAFT;
  const SummaryRow = ({ k, v }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px dashed ${C.border}`, fontSize: 13 }}>
      <span style={{ color: C.muted, flex: '0 0 auto' }}>{k}</span>
      <span style={{ color: C.text, textAlign: 'right', fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>{v}</span>
    </div>
  );
  const right = (
    <div style={{ position: wide ? 'sticky' : 'static', top: 0, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 10 }}>Xem trước thông báo</div>
        <div style={{ background: '#eef0f4', borderRadius: 14, padding: 14 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 2px rgba(16,24,40,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 6 }}>
              <span style={{ fontWeight: 600, letterSpacing: 0.4 }}>EVG</span>
              <span>{previewTime || 'bây giờ'}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflowWrap: 'anywhere' }}>{values.title || 'Tiêu đề thông báo'}</div>
            <div style={{ fontSize: 13, color: '#344054', marginTop: 2, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {values.message || 'Nội dung thông báo sẽ hiển thị ở đây.'}
            </div>
            {values.link ? <div style={{ fontSize: 12, color: C.primary, marginTop: 8, overflowWrap: 'anywhere' }}>Liên kết: {values.link}</div> : null}
          </div>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Tóm tắt chiến dịch</div>
        <SummaryRow k="Đối tượng" v={roleLabel(values)} />
        <SummaryRow k="Điều kiện" v={filterText} />
        <SummaryRow k="Người nhận dự kiến" v={audienceState === 'error' ? '—' : audience ? `${fmtNumber(audience.total)} người` : '…'} />
        <SummaryRow k="Tần suất" v={frequencyText} />
        <SummaryRow k="Thời gian" v={timeText} />
        <SummaryRow k="Không gửi lại" v={Number(values.cooldownDays) > 0 ? `${values.cooldownDays} ngày` : 'Không giới hạn'} />
        <SummaryRow k="Trạng thái" v={<Tag color={statusMeta.color} style={{ marginInlineEnd: 0 }}>{statusMeta.label}</Tag>} />
      </div>
    </div>
  );

  const activateLabel = status === 'PAUSED' ? 'Lưu và tiếp tục chạy' : 'Kích hoạt chiến dịch';
  const activateButton = (
    <Button type="primary" size="large" loading={saving} disabled={!valid && showErrors ? true : false} onClick={confirmActivate}>
      {activateLabel}
    </Button>
  );

  return (
    <Modal
      open={open} onCancel={onClose} maskClosable={false} centered destroyOnClose zIndex={MODAL_Z_INDEX}
      width="min(1140px, calc(100vw - 64px))"
      styles={{ body: { maxHeight: '64vh', overflowY: 'auto', padding: '4px 4px 8px' }, content: { borderRadius: 12, padding: '20px 24px' } }}
      title={(
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: C.text }}>{campaign ? `Sửa chiến dịch #${campaign.campaignId}` : 'Tạo chiến dịch thông báo'}</div>
          <div style={{ fontSize: 13, fontWeight: 400, color: C.muted, marginTop: 4 }}>Thiết lập nội dung, đối tượng nhận và lịch gửi thông báo.</div>
        </div>
      )}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderTop: `1px solid ${C.border}`, paddingTop: 16, marginTop: 8, flexWrap: 'wrap' }}>
          <Button type="text" size="large" onClick={onClose}>Hủy</Button>
          <Space wrap>
            <Button size="large" onClick={sendTest} disabled={!values.title || !values.message}>Gửi thử cho tôi</Button>
            {status === 'ACTIVE' ? (
              <Button type="primary" size="large" loading={saving} onClick={() => submit('draft')}>Lưu thay đổi</Button>
            ) : (
              <>
                <Button size="large" loading={saving} onClick={() => submit('draft')}>{status === 'PAUSED' ? 'Lưu thay đổi' : 'Lưu bản nháp'}</Button>
                {!valid && showErrors
                  ? <Tooltip title={`Còn thiếu: ${missing.join('; ')}`}>{activateButton}</Tooltip>
                  : activateButton}
              </>
            )}
          </Space>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', gap: 32, alignItems: 'stretch', paddingTop: 8 }}>
        <div style={{ flex: wide ? '1 1 66%' : '1 1 auto', minWidth: 0 }}>{left}</div>
        {wide ? <div style={{ width: 1, background: C.border, flex: '0 0 1px' }} /> : null}
        <div style={{ flex: wide ? '0 0 34%' : '1 1 auto', minWidth: 0 }}>{right}</div>
      </div>
    </Modal>
  );
}

function RunsModal({ campaign, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!campaign) return;
    setLoading(true);
    notificationCampaignApi.getRuns(campaign.campaignId)
      .then((response) => setRuns(response?.runs || []))
      .catch((e) => ToastHelper.showError(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [campaign]);
  const columns = [
    { title: 'Giờ gửi', dataIndex: 'scheduledFor', render: (v) => moment(v).format('HH:mm DD/MM/YYYY') },
    { title: 'Trạng thái', dataIndex: 'status', render: (v) => <Tag color={RUN_META[v]?.color}>{RUN_META[v]?.label || v}</Tag> },
    { title: 'Mục tiêu', dataIndex: 'targetCount', align: 'right' },
    { title: 'Đã gửi', dataIndex: 'sentCount', align: 'right' },
    { title: 'Lỗi', dataIndex: 'failedCount', align: 'right' },
    { title: 'Đã mở', dataIndex: 'openedCount', align: 'right', render: (v, row) => (row.sentCount ? `${v} (${Math.round((v / row.sentCount) * 100)}%)` : '0') },
    { title: 'Không có thiết bị', dataIndex: 'noDeviceCount', align: 'right' },
    { title: 'Bỏ qua (tắt KM / đã nhận)', align: 'right', render: (_, row) => `${row.skippedOptOut || 0} / ${row.skippedCapped || 0}` },
    { title: 'Ghi chú', dataIndex: 'error', render: (v) => v || '' },
  ];
  return (
    <Modal open={!!campaign} onCancel={onClose} footer={null} width={860} zIndex={MODAL_Z_INDEX} title={`Lịch sử gửi · ${campaign?.name || ''}`}>
      <Table rowKey="runId" size="small" loading={loading} columns={columns} dataSource={runs} pagination={{ pageSize: 10 }} />
    </Modal>
  );
}

function NotificationCampaignTabView() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({ open: false, campaign: null });
  const [runsFor, setRunsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationCampaignApi.getCampaigns({ page, limit: 20 });
      setRows(response?.campaigns || []);
      setTotal(response?.total || 0);
    } catch (e) { ToastHelper.showError(errorMessage(e)); } finally { setLoading(false); }
  }, [page]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, successText) => {
    try { await fn(); ToastHelper.showSuccess(successText); load(); } catch (e) { ToastHelper.showError(errorMessage(e)); }
  };

  const columns = useMemo(() => [
    { title: 'Chiến dịch', render: (_, row) => (<div><Text strong>{row.name}</Text><br /><Text type="secondary">{row.title}</Text></div>) },
    { title: 'Gửi cho', render: (_, row) => `${roleLabel(row)}${filterLabel(row)}` },
    { title: 'Lịch', render: (_, row) => scheduleLabel(row) },
    { title: 'Trạng thái', dataIndex: 'status', render: (v) => <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label || v}</Tag> },
    {
      title: 'Lần gửi gần nhất',
      render: (_, row) => (row.lastRun
        ? (
          <div>
            {moment(row.lastRun.scheduledFor).format('HH:mm DD/MM')}
            <br />
            <Text type="secondary">{row.lastRun.sentCount}/{row.lastRun.targetCount} đã gửi{row.lastRun.failedCount ? ` · ${row.lastRun.failedCount} lỗi` : ''}</Text>
          </div>
        )
        : <Text type="secondary">Chưa gửi</Text>),
    },
    {
      title: '',
      render: (_, row) => (
        <Space wrap>
          {['DRAFT', 'PAUSED'].includes(row.status) && (
            <Button
              size="small" type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }}
              onClick={() => act(() => (row.status === 'DRAFT' ? notificationCampaignApi.activate(row.campaignId) : notificationCampaignApi.resume(row.campaignId)), 'Đã kích hoạt')}
            >
              {row.status === 'DRAFT' ? 'Kích hoạt' : 'Tiếp tục'}
            </Button>
          )}
          {row.status === 'ACTIVE' && (
            <Button size="small" onClick={() => act(() => notificationCampaignApi.pause(row.campaignId), 'Đã tạm dừng')}>Tạm dừng</Button>
          )}
          {row.status !== 'ENDED' && <Button size="small" onClick={() => setFormState({ open: true, campaign: row })}>Sửa</Button>}
          <Button size="small" onClick={() => setRunsFor(row)}>Lịch sử</Button>
          {row.status === 'DRAFT' && (
            <Popconfirm title="Xóa chiến dịch nháp này?" onConfirm={() => act(() => notificationCampaignApi.deleteCampaign(row.campaignId), 'Đã xóa')}>
              <Button size="small" danger>Xóa</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [load]);

  return (
    <div className="px-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <Text strong style={{ fontSize: 18 }}>Thông báo tự động</Text>
          <br />
          <Text type="secondary">Soạn một lần, hệ thống tự gửi theo lịch (một lần, hằng ngày, tuần, tháng). Tổng: {total}</Text>
        </div>
        <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={() => setFormState({ open: true, campaign: null })}>Tạo chiến dịch</Button>
      </div>
      <Table
        rowKey="campaignId" loading={loading} columns={columns} dataSource={rows}
        pagination={{ current: page + 1, pageSize: 20, total, onChange: (p) => setPage(p - 1), showSizeChanger: false }}
      />
      <CampaignFormModal
        open={formState.open} campaign={formState.campaign}
        onClose={() => setFormState({ open: false, campaign: null })}
        onSaved={() => { setFormState({ open: false, campaign: null }); load(); }}
      />
      <RunsModal campaign={runsFor} onClose={() => setRunsFor(null)} />
    </div>
  );
}

export default NotificationCampaignTabView;
